import { Fragment, useEffect, useState } from 'react';
import {
  intelDistress,
  intelFacilities,
  intelOperators,
  intelPaperBook,
  intelPaperPosition,
  intelProspectCreate,
  intelProspects,
  intelUnderwrite,
  type OperatorBuilding,
  intelReach,
  type CatalogReach,
  type DistressResponse,
  type FacilitiesPage,
  type FacilityUnderwrite,
  type OperatorsResponse,
  type PaperBook,
  type PaperPositionDetail,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { A, CAP, CAP9, chip, DASH, n0, onSet, plusK, useBreakdown, type IntelState, type TabProps } from './common';
import { GC, M, MINUS, pc, gradeOf, dealsOf, type Deal, type Grade } from './model';
import { OperatorDrawer } from './OperatorDrawer';
import { usePeerStaffing, peerOf, staffingLine } from './staffing';
import { NsMap, NS_NAMES, STARTING } from './northstar/NsMap';
import './northstar/northstar.css';
import { BuildLabel, useBuildText } from '../../ui/buildLabels';

/**
 * A building handed to the Deal Desk by the Relationship Map (V4 atlas-open-deal detail):
 * only what the map knows about it. Payer revenue `u` is $M a year; occupancy in percent.
 */
export interface NsDealSrc {
  id?: string;
  name: string;
  beds?: number;
  occ?: number;
  trend?: number;
  role?: string;
  payers?: { n: string; u: number }[];
  /** The server's label on the payer figures: 'COST REPORT FY2024', 'MODELED' or 'ESTIMATED'. */
  payersLabel?: string;
  referrals?: { n: string; sr?: number }[];
}
/** North Star's own keys in the page state. */
interface NsKeys {
  nsMapSel?: string | null;
  nsDealFrom?: string;
  nsDealSrc?: NsDealSrc | null;
}
type NsState = IntelState & NsKeys;

/** The state list the Operators filter offers. */
const OP_STATES = Object.keys(NS_NAMES).sort();
const LENS_SORT: Record<string, 'opportunity' | 'score' | 'market'> = { Opportunity: 'opportunity', Quality: 'score', Market: 'market' };
const cellStyle = (val: number) =>
  'box-sizing:border-box;padding:6px 0;border-radius:6px;text-align:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;' +
  (val >= 75 ? 'background:rgba(47,138,111,.15);color:#1F6B52;' : val >= 55 ? 'background:rgba(79,138,214,.14);color:#2F5C97;' : val >= 35 ? 'background:rgba(91,107,124,.13);color:#5B6B7C;' : 'background:rgba(179,38,30,.12);color:#B3261E;');
const num = (v: unknown) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const VIEWS = ['Ranked', 'Operators', 'Distress', 'Map', 'Deal Desk', 'Paper Book'];
const TAG = 'font-size:14px;font-weight:700;letter-spacing:.07em;color:#3C454F;background:rgba(34,39,46,.08);border-radius:4px;padding:2px 6px;';

/** North Star (V4 aiIsNs, T15252; aiNsVals L30614): the market, mapped. */
export function NorthStarTab(p: TabProps) {
  const { set, client } = p;
  const st = p.st as NsState;
  const view = st.nsView && VIEWS.includes(st.nsView) ? st.nsView : 'Map';
  const lens = st.nsLens || 'Opportunity';
  const bd = useBreakdown(client);
  const reach = useQuery<CatalogReach>((signal) => intelReach(client, { signal }), [client]);
  const total = bd.data?.total ?? null;
  const r = reach.data;
  const stat = (x: { count: number; static: boolean } | undefined) => (x ? n0(x.count) : DASH);
  const stats: [string, string, string, boolean][] = [
    [total == null ? DASH : plusK(total), 'Facilities mapped', 'Every US senior-care site', false],
    [stat(r?.enriched), 'With contact details', 'Residential · names and phones', !!r?.enriched.static],
    [stat(r?.decisionMakers), 'Decision-makers', 'Named operator contacts in the CRM', !!r?.decisionMakers.static],
  ];

  const prospects = p.book.data?.prospects ?? [];
  const held = p.deals.filter((x) => x.status === 'Acquired');
  const count = (fn: (x: (typeof prospects)[number]) => boolean) => prospects.filter(fn).length;
  const pipeline: [string, number][] = [
    ['Simulated', count((x) => x.lifecycle_stage === 'simulated')],
    ['Pilot', count((x) => x.lifecycle_stage === 'pilot')],
    ['Live', count((x) => x.lifecycle_stage === 'live')],
    ['Customers', count((x) => x.outcome === 'customer')],
    ['Acquired', count((x) => x.status === 'acquired')],
  ];
  const zeros = pipeline[1][1] === 0 && pipeline[2][1] === 0 && pipeline[3][1] === 0;

  return (
    <div style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 17px;')}>
        <div style={sx(CAP)}>The market, mapped</div>
        <div style={sx('font-size:16px;font-weight:600;margin-top:4px;')}>We have mapped the entire US senior-care market.</div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-top:6px;')}>
          {stats.map(([val, label, note, isStatic]) => (
            <div key={label}>
              <div style={sx('display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;row-gap:6px;')}>
                <span style={sx('font-size:26px;font-weight:700;letter-spacing:-.025em;color:#2F5C97;')}>{val}</span>
                {isStatic && (
                  <span title="A fixed figure in the current build, not re-counted when the catalog refreshes" style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#54606C;background:rgba(34,39,46,.12);border-radius:3px;padding:2px 5px;')}>
                    STATIC
                  </span>
                )}
              </div>
              <div style={sx(CAP9 + 'margin-top:2px;')}>{label}</div>
              <div style={sx('font-size:14px;color:#54606C;')}>{note}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
        {VIEWS.map((x) => (
          <A key={x} onClick={onSet(set, { nsView: x, nsDealSrc: null, nsDealFrom: '' })} style={chip(view === x)}>
            {x === 'Paper Book' ? 'Deal book' : x}
          </A>
        ))}
        <span style={sx('flex:1;')} />
        {view === 'Operators' && (
          // Restored from production: search the owners by name (GET /operators?q=).
          <input
            type="search"
            aria-label="Search operators by name"
            placeholder="Search owners…"
            value={st.nsOpQ ?? ''}
            onChange={(e) => set({ nsOpQ: e.target.value })}
            style={sx('box-sizing:border-box;height:24px;width:220px;padding:0 9px;border-radius:6px;border:1px solid rgba(34,39,46,.14);background:transparent;font:inherit;font-size:14px;color:#22272E;outline:none;')}
          />
        )}
        {view === 'Operators' && (
          // Restored from production: the state filter and the sort (GET /operators?state=&sort=).
          <>
            <select aria-label="Operators in state" value={st.nsOpState ?? ''} onChange={(e) => set({ nsOpState: e.target.value })} style={sx(SELECT)}>
              <option value="">All states</option>
              {OP_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select aria-label="Sort operators by" value={st.nsOpSort ?? 'facilities'} onChange={(e) => set({ nsOpSort: e.target.value })} style={sx(SELECT)}>
              {OP_SORTS.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </>
        )}
        {view !== 'Map' &&
          ['Opportunity', 'Quality', 'Market'].map((x) => (
            <A
              key={x}
              onClick={onSet(set, { nsLens: x })}
              style={
                'display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;text-decoration:none;white-space:nowrap;' +
                (lens === x ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;' : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);')
              }
            >
              {x}
            </A>
          ))}
      </div>

      {view === 'Map' && <MapView p={p} total={total} byState={bd.data?.byState ?? null} loading={bd.loading && !bd.data} />}
      {view === 'Ranked' && <Ranked p={p} lens={lens} />}
      {view === 'Operators' && <Operators p={p} />}
      {view === 'Distress' && <Distress p={p} />}
      {view === 'Deal Desk' &&
        (st.nsDealSrc || p.f ? <DealDesk p={p} f={p.f} /> : <EmptyState title="No facility on the deal desk" body="Track a facility from the catalog and underwrite it here." />)}
      {view === 'Paper Book' && <PaperBookView p={p} held={held} />}

      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 17px;')}>
        <div style={sx(CAP + 'margin-bottom:6px;')}>Market map &rarr; working pipeline</div>
        <div style={sx('display:flex;align-items:stretch;gap:9px;flex-wrap:wrap;')}>
          {(
            [
              ['TAM', total == null ? DASH : plusK(total), 'Universe'],
              ['Scored', r ? plusK(r.scored) : DASH, 'Composite'],
              ['Enriched', stat(r?.enriched), 'Contactable'],
              ['Tracked', stat(r?.tracked), 'In CRM'],
              ['Reachable', stat(r?.decisionMakers), 'Contacts'],
            ] as [string, string, string][]
          ).map(([label, val, note]) => (
            <div key={label} style={sx('box-sizing:border-box;flex:1;min-width:120px;padding:2px 13px 0 0;')}>
              <div style={sx(CAP9)}>{label}</div>
              <div style={sx('font-size:16px;font-weight:700;letter-spacing:-.02em;')}>{val}</div>
              <div style={sx('font-size:14px;color:#54606C;')}>{note}</div>
            </div>
          ))}
        </div>
        <div style={sx('display:flex;align-items:center;gap:18px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-top:7px;padding-top:7px;border-top:1px solid rgba(34,39,46,.1);')}>
          {pipeline.map(([label, n]) => (
            <span key={label} style={sx('display:inline-flex;align-items:baseline;gap:7px;')}>
              <span style={sx('font-size:17px;font-weight:700;')}>{p.book.data ? String(n) : DASH}</span>
              <span style={sx(CAP9)}>{label}</span>
            </span>
          ))}
          <span style={sx('flex:1;')} />
          {p.book.data && zeros && <span style={sx('font-size:14px;color:#54606C;')}>Zeros are real - nothing has reached pilot or live yet.</span>}
        </div>
      </div>
    </div>
  );
}

const MAP_H = 'height:clamp(280px,calc(100vh - 575px),600px);';
const scoreBadge = (o: number) =>
  'flex:0 0 34px;height:24px;display:inline-flex;align-items:center;justify-content:center;border-radius:6px;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;' +
  (o >= 75 ? 'background:rgba(47,138,111,.15);color:#1F6B52;' : o >= 55 ? 'background:rgba(79,138,214,.14);color:#2F5C97;' : o >= 35 ? 'background:rgba(91,107,124,.13);color:#5B6B7C;' : 'background:rgba(179,38,30,.12);color:#B3261E;');

/**
 * Facilities per state (V4 nsIsMap): the US map shaded by the catalog's count per state
 * (GET /facilities/breakdown byState, live), and the panel for the selected state: its count,
 * share, and the deal book's top opportunities there.
 */
function MapView({ p, total, byState, loading }: { p: TabProps; total: number | null; byState: { key: string; count: number }[] | null; loading: boolean }) {
  const st = p.st as NsState;
  const sel = st.nsMapSel || null;
  const counts: Record<string, number> = {};
  for (const b of byState ?? []) if (NS_NAMES[b.key]) counts[b.key] = b.count;
  const sum0 = Object.values(counts).reduce((t, x) => t + x, 0);
  const n = sel ? counts[sel] ?? 0 : null;
  const name = (s: string | null) => (s ? NS_NAMES[s] || s : '');
  const inSt = sel ? p.deals.filter((x) => x.stt === sel) : p.deals;
  const top = inSt.slice().sort((a, b) => b.comp - a.comp).slice(0, 12);
  const selStats: [string, string][] = [
    ['Facilities', n != null ? n.toLocaleString('en-US') : total == null ? DASH : plusK(total)],
    ['Share of US', n != null ? (sum0 ? ((n / sum0) * 100).toFixed(1) : '0.0') + '%' : '100%'],
    ['Scored in this list', String(inSt.length)],
    ['Opportunity 75+', String(inSt.filter((x) => x.comp >= 75).length)],
  ];
  // Find searches any state's full catalog (national first), not only the starting markets.
  const canFind = !!sel;
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
      <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-bottom:12px;')}>
        <span style={sx(CAP)}>Facilities per state</span>
        <span title="Counted live from the facility catalog (CMS and NPPES)" style={sx(TAG)}>LIVE</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>Click a state to zoom in</span>
      </div>
      <div style={sx('display:grid;grid-template-columns:minmax(0,3fr) minmax(190px,1fr);gap:12px;align-items:stretch;min-width:0;')}>
        {loading ? (
          <div style={sx('position:relative;min-width:0;overflow:hidden;' + MAP_H)}>
            <div style={sx('display:flex;align-items:center;justify-content:center;height:100%;font-size:16px;color:#54606C;')}>Loading the US map…</div>
          </div>
        ) : (
          <NsMap counts={counts} sel={sel} style={MAP_H} onPick={(ab) => p.set({ nsMapSel: st.nsMapSel === ab ? null : ab })} />
        )}
        <div style={sx('min-width:0;display:flex;flex-direction:column;gap:10px;' + MAP_H)}>
          <div style={sx('flex:0 0 auto;border-bottom:1px solid rgba(34,39,46,.12);padding-bottom:10px;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{sel ? name(sel) : 'United States'}</span>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.07em;color:#2F5C97;background:rgba(79,138,214,.13);border-radius:4px;padding:2px 6px;')}>{sel && STARTING.has(sel) ? 'STARTING STATE' : 'LIVE'}</span>
              <span style={sx('flex:1;')} />
              {sel && (
                <a href="#" onClick={(e) => { e.preventDefault(); p.set({ nsMapSel: null }); }} style={sx('font-size:14px;color:#2F5C97;text-decoration:none;font-weight:600;')}>
                  All states
                </a>
              )}
            </div>
            <div style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 12px;margin-top:9px;')}>
              {selStats.map(([k, v]) => (
                <div key={k} style={sx('min-width:0;')}>
                  <div style={sx('font-size:14px;color:#54606C;')}>{k}</div>
                  <div style={sx('font-size:17px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;')}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={sx('flex:0 0 auto;font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:600;')}>{sel ? 'Top opportunities in ' + name(sel) : 'Top opportunities nationally'}</div>
          <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;')}>
            {top.map((x) => (
              <a
                key={x.id}
                href="#"
                className="intel-h-nsrow"
                onClick={(e) => {
                  e.preventDefault();
                  p.set({ aiSel: p.deals.indexOf(x), nsView: 'Deal Desk', nsDealSrc: null, nsDealFrom: 'North Star map' });
                }}
                style={sx('display:flex;align-items:center;gap:9px;padding:8px 6px;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:#22272E;border-radius:6px;')}
              >
                <span style={sx(scoreBadge(x.comp))}>{String(Math.round(x.comp))}</span>
                <span style={sx('min-width:0;flex:1 1 auto;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:600;overflow-wrap:break-word;')}>{x.name}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;')}>{x.city + ', ' + x.stt}</span>
                </span>
                <span style={sx('font-size:14px;color:#2F5C97;font-weight:600;white-space:nowrap;')}>Open</span>
              </a>
            ))}
            {top.length === 0 && (
              <>
                <div style={sx('font-size:16px;color:#54606C;line-height:1.5;padding:8px 2px;')}>
                  {canFind
                    ? 'This short scored list has no ' + name(sel) + ' facilities yet. Find searches the full ' + name(sel) + ' catalog.'
                    : 'None of the scored facilities in this list are in ' + (sel ? name(sel) : 'this area') + ' yet. Scoring fills in as cost reports load.'}
                </div>
                {canFind && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      p.go('find', { fdSt: sel });
                    }}
                    style={sx('display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;text-decoration:none;align-self:flex-start;margin-top:4px;')}
                  >
                    {'Search ' + name(sel) + ' in Find'}
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:10px;')}>
        Shade = facility count per state, counted live from the catalog. Click a state to zoom and list its top opportunities. Texas and Florida are outlined as our starting states.
      </div>
    </div>
  );
}

function Ranked({ p, lens }: { p: TabProps; lens: string }) {
  const q = useQuery<FacilitiesPage>((signal) => intelFacilities(p.client, { query: { sort: LENS_SORT[lens], limit: 25 }, signal }), [p.client, lens]);
  const cols = 'display:grid;grid-template-columns:minmax(0,2.4fr) 56px repeat(3,minmax(0,1fr));gap:10px;';
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 17px;')}>
      <div style={sx(cols + CAP9 + 'padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.1);')}>
        <span>Facility</span>
        <span style={sx('text-align:right;')}>Beds</span>
        <span style={sx('text-align:center;')}>Opportunity</span>
        <span style={sx('text-align:center;')}>Quality</span>
        <span style={sx('text-align:center;')}>Market</span>
      </div>
      {q.loading && !q.data && <Skeleton rows={6} label="Ranking the catalog" />}
      {q.error && !q.data && <ErrorState error={q.error} onRetry={q.reload} />}
      {(q.data?.facilities ?? []).map((r) => (
        <div key={r.id} style={sx(cols + 'align-items:center;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:600;')}>{r.name}</span>
            <span style={sx('display:block;font-size:14px;color:#54606C;')}>{(r.city ?? DASH) + ', ' + (r.state ?? DASH)}</span>
          </span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:14px;')}>{r.beds == null ? DASH : String(r.beds)}</span>
          {[r.opportunity_score, r.composite_score, r.market_score].map((v, j) => {
            const val = num(v);
            return (
              <span key={j} style={sx(val == null ? cellStyle(0).replace(/background:[^;]+;color:[^;]+;/, 'background:rgba(34,39,46,.05);color:#54606C;') : cellStyle(Math.round(val)))}>
                {val == null ? DASH : String(Math.round(val))}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const OP_PAGE = 25;
type OpSort = 'facilities' | 'beds' | 'distress' | 'opportunity' | 'rating' | 'name';
const OP_SORTS: [OpSort, string][] = [
  ['facilities', 'Most facilities'], ['beds', 'Most beds'], ['distress', 'Most distress'],
  ['opportunity', 'Opportunity'], ['rating', 'CMS rating'], ['name', 'Name'],
];
const SELECT = 'box-sizing:border-box;height:24px;padding:0 6px;border-radius:6px;border:1px solid rgba(34,39,46,.14);background:transparent;font:inherit;font-size:14px;color:#22272E;';

/**
 * "Send to Deal Desk" from a building in the ownership network: open it when it is
 * already on the deal book, else put it on the book (POST /prospects) first. The Deal
 * Desk runs only on buildings the catalog can carry the model for; any other stays on
 * Tracking and the page says why. Returns a line to show, or null when it opened.
 */
export async function sendToDealDesk(p: TabProps, f: OperatorBuilding): Promise<string | null> {
  const ids = new Set([f.id, f.ccn, f.id.includes(':') ? f.id.split(':')[1] : null].filter((x): x is string => !!x));
  const match = (pid: string | null | undefined) => !!pid && (ids.has(pid) || ids.has(pid.includes(':') ? pid.split(':')[1] : 'cms:' + pid));
  let prospects = p.book.data?.prospects ?? [];
  if (!prospects.some((x) => match(x.provider_id))) {
    if (p.readOnly) return 'Read only: this building is not on the deal book, and a Partner cannot add it.';
    await intelProspectCreate(p.client, { body: { providerId: f.ccn ?? f.id, name: f.name, state: f.state } });
    const fresh = await intelProspects(p.client, {});
    p.book.setData(() => fresh);
    prospects = fresh.prospects;
  }
  const i = dealsOf(prospects).findIndex((d) => match(d.providerId));
  if (i < 0) return 'On the deal book. The catalog cannot carry the underwriting model for this building yet (no staffing hours or cost report), so it waits on Tracking, not the Deal Desk.';
  p.set({ aiSel: i, nsView: 'Deal Desk' });
  return null;
}

/** The search typed into the strip, settled for 300 ms before it is asked for. */
function useSettled(v: string, ms = 300): string {
  const [out, setOut] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setOut(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return out;
}

function Operators({ p }: { p: TabProps }) {
  // Restored from production: an operator's row opens its detail beneath it; the owners
  // are searchable by name (a search also finds single-building owners) and paged.
  const [open, setOpen] = useState<string | null>(null);
  const search = useSettled((p.st.nsOpQ ?? '').trim());
  const [offset, setOffset] = useState(0);
  const state = p.st.nsOpState || undefined;
  const sort = (OP_SORTS.find(([k]) => k === p.st.nsOpSort)?.[0] ?? 'facilities');
  useEffect(() => setOffset(0), [search, state, sort]);
  const q = useQuery<OperatorsResponse>(
    (signal) =>
      intelOperators(p.client, {
        query: { ...(search ? { q: search, minFacilities: 1 } : {}), ...(state ? { state } : {}), ...(sort !== 'facilities' ? { sort } : {}), limit: OP_PAGE, offset },
        signal,
      }),
    [p.client, search, state, sort, offset],
  );
  const total = q.data?.total ?? 0;
  const cols = 'display:grid;grid-template-columns:minmax(0,2.4fr) 82px 92px 62px 40px;gap:10px;';
  const rows = (q.data?.operators ?? []).map((o) => {
    const comp = num(o.avg_composite);
    const g = o.grade ?? (comp == null ? '-' : gradeOf(comp));
    return { name: o.owner_org, fac: o.facility_count, beds: o.total_beds, states: o.state_count, grade: g, real: true };
  });
  const ind = search ? null : q.data?.independent;
  if (ind) rows.push({ name: 'Independent owner-operators', fac: ind.facility_count, beds: ind.total_beds, states: ind.state_count, grade: '-', real: false });
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 17px;')}>
      <div style={sx(cols + CAP9 + 'padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.1);')}>
        <span>Operator</span>
        <span style={sx('text-align:right;')}>Facilities</span>
        <span style={sx('text-align:right;')}>Beds</span>
        <span style={sx('text-align:right;')}>States</span>
        <span style={sx('text-align:right;')}>Grade</span>
      </div>
      {q.loading && !q.data && <Skeleton rows={6} label="Loading the ownership graph" />}
      {q.error && !q.data && <ErrorState error={q.error} onRetry={q.reload} />}
      {rows.map((o) => {
        const c = GC[o.grade as Grade];
        return (
          <Fragment key={o.name}>
          <div style={sx(cols + 'align-items:center;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
            {o.real ? (
              <a href="#" aria-expanded={open === o.name} onClick={(e) => { e.preventDefault(); setOpen(open === o.name ? null : o.name); }} style={sx('font-size:16px;font-weight:600;color:#22272E;text-decoration:none;')}>{o.name}</a>
            ) : (
              <span style={sx('font-size:16px;font-weight:600;')}>{o.name}</span>
            )}
            <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:14px;')}>{n0(o.fac)}</span>
            <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:14px;')}>{n0(o.beds)}</span>
            <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:14px;')}>{String(o.states)}</span>
            <span style={sx('text-align:right;')}>
              <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;font-size:14px;font-weight:700;color:' + (c ? '#fff' : '#54606C') + ';background:' + (c || 'rgba(34,39,46,.07)') + ';')}>{o.grade}</span>
            </span>
          </div>
          {open === o.name && <OperatorDrawer client={p.client} org={o.name} onDealDesk={p.sales ? undefined : (f) => sendToDealDesk(p, f)} />}
          </Fragment>
        );
      })}
      {q.data && search && rows.length === 0 && (
        <div style={sx('font-size:16px;color:#54606C;padding:10px 0;')}>{`No owner in the CMS catalog matches "${search}".`}</div>
      )}
      {q.data && total > OP_PAGE && (
        <div style={sx('display:flex;align-items:center;gap:10px;margin-top:8px;font-size:14px;color:#54606C;')}>
          <span>{`${n0(offset + 1)}–${n0(Math.min(offset + OP_PAGE, total))} of ${n0(total)} operators`}</span>
          {offset > 0 && <A onClick={(e) => { e.preventDefault(); setOffset(Math.max(0, offset - OP_PAGE)); }} style={chip(false)}>Previous</A>}
          {offset + OP_PAGE < total && <A onClick={(e) => { e.preventDefault(); setOffset(offset + OP_PAGE); }} style={chip(false)}>Next</A>}
        </div>
      )}
      <div style={sx('font-size:14px;color:#54606C;margin-top:10px;')}>Ownership graph from NPPES + CMS affiliation data. Independent owner-operators are the residual, not a chain.</div>
    </div>
  );
}

function Distress({ p }: { p: TabProps }) {
  const q = useQuery<DistressResponse>((signal) => intelDistress(p.client, { query: { limit: 25 }, signal }), [p.client]);
  const d = q.data;
  const head = d ? n0(d.total) + ' flagged' + (d.ratedCount != null ? ' of ' + n0(d.ratedCount) + ' rated SNFs' : '') : DASH;
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 17px;')}>
      <div style={sx(CAP + 'margin-bottom:10px;')}>{head}</div>
      {q.loading && !d && <Skeleton rows={6} label="Running the distress screen" />}
      {q.error && !d && <ErrorState error={q.error} onRetry={q.reload} />}
      {d && d.facilities.length === 0 && <EmptyState compact title="Nothing flagged" body="No rated facility clears the distress screen right now." />}
      {(d?.facilities ?? []).map((x) => (
        <div key={x.id} style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
            <span style={sx('display:block;font-size:16px;font-weight:600;')}>{x.name}</span>
            <span style={sx('display:block;font-size:14px;color:#54606C;')}>
              {(x.city ?? DASH) + ', ' + (x.state ?? DASH)} &middot; {x.beds == null ? DASH : x.beds} beds
            </span>
          </span>
          <span style={sx('font-size:14px;color:#B3261E;text-align:right;')}>{x.reasons.join(' · ')}</span>
        </div>
      ))}
    </div>
  );
}

/** The selected facility's paper position (mark and realized history), when one is open. */
function usePosition(p: TabProps, f: Deal | null) {
  const book = useQuery<PaperBook>((signal) => intelPaperBook(p.client, { signal }), [p.client], { enabled: !!f });
  const pos = f?.providerId ? book.data?.positions.find((x) => x.facilityId === f.providerId && x.status === 'open') ?? null : null;
  const detail = useQuery<PaperPositionDetail>((signal) => intelPaperPosition(p.client, { params: { id: pos!.id }, signal }), [p.client, pos?.id], { enabled: !!pos });
  return pos ? detail.data?.drift ?? null : null;
}

/** $ the Deal Desk's map way: $4.3M, $12M, $850K. */
const mm = (x: number) => '$' + (x >= 1 ? x.toFixed(x < 10 ? 1 : 0) + 'M' : Math.round(x * 1000) + 'K');
const MAP_W = 0.112;
const MAP_G = 0.025;
const MAP_GROW = [0.084, 0.065, 0.053, 0.04, 0.031];
const MAP_MARGIN = 0.1;
const MAP_CONV = 0.6;
const MAP_LEV = 0.6;

/**
 * A map building underwritten from what the map knows (V4 nsDDRows for nsDealSrc): revenue is
 * the sum of its payer rows; EBITDA at an assumed 10% margin, or the facility's HCRIS
 * operating margin when its cost report is on file (GET /facilities/:id/underwrite, measured);
 * a 5-year DCF on FCF = 60% of EBITDA at WACC 11.2%, g 2.5%; debt capacity 60%. MODELED EST.
 * No BUY or PASS without an asking price.
 */
export function mapDeskRows(d: NsDealSrc, dd: string, hcrisMargin: number | null): [string, string][] {
  const rev = (d.payers || []).reduce((s, x) => s + (x.u || 0), 0);
  if (!rev) return [['Revenue', 'not known yet'], ['Why', 'No payer figures on the map for this building yet'], ['Next step', 'Load its cost report, then underwrite']];
  const margin = hcrisMargin ?? MAP_MARGIN;
  const e0 = rev * margin;
  let e = e0;
  let pv = 0;
  MAP_GROW.forEach((g, i) => {
    e = e * (1 + g);
    pv += (e * MAP_CONV) / Math.pow(1 + MAP_W, i + 1);
  });
  const ev = pv + (e * MAP_CONV * (1 + MAP_G)) / (MAP_W - MAP_G) / Math.pow(1 + MAP_W, 5);
  const mc = (d.payers || []).filter((x) => /medicaid/i.test(x.n)).reduce((s, x) => s + x.u, 0);
  const ebitdaRow: [string, string] =
    hcrisMargin == null
      ? ['EBITDA at assumed 10% margin', mm(e0) + '/yr · MODELED EST.']
      : ['EBITDA at its HCRIS ' + (hcrisMargin * 100).toFixed(1) + '% margin', mm(e0) + '/yr · cost report margin'];
  if (dd === 'Underwrite')
    return [
      ['Revenue (from payer mix)', mm(rev) + '/yr'],
      ebitdaRow,
      ['DCF value (11.2% WACC, 2.5% g)', mm(ev) + ' · MODELED EST.'],
      ['Debt capacity at 60%', mm(ev * MAP_LEV)],
      ['Verdict', 'Needs an asking price to call BUY or PASS'],
    ];
  if (dd === 'Value-Creation')
    return [
      ['Occupancy headroom', d.occ ? Math.max(0, 95 - d.occ) + ' pts to 95%' : 'not known yet'],
      ['Revenue at 95% occupancy', d.occ ? mm((rev * 95) / d.occ) + '/yr · MODELED EST.' : 'not known yet'],
      ['Top referral source', (d.referrals || [])[0]?.n || 'not known yet'],
    ];
  if (dd === 'Risk')
    return [
      ['Medicaid share of revenue', Math.round((mc / rev) * 100) + '%'],
      ['Occupancy trend', d.trend != null ? (d.trend >= 0 ? '▲ ' : '▼ ') + Math.abs(d.trend) + '%' : 'not known yet'],
      ['Stress at ' + MINUS + '18% value', mm(ev * 0.82) + ' · MODELED EST.'],
    ];
  return [
    ['Status', 'Not owned, so no hold, sell or performance record'],
    ['Next step', 'Track it, then this tab fills from the paper book'],
  ];
}

/** The map facts strip (V4 nsDealFacts). */
export function mapDeskFacts(d: NsDealSrc | null | undefined): { k: string; v: string; s: string; c: string }[] {
  if (!d) return [];
  const fm = (x: number) => '$' + (x >= 1 ? x.toFixed(x < 10 ? 1 : 0) + 'M' : Math.round(x * 1000) + 'K') + '/yr';
  const out = [];
  out.push({ k: 'Beds', v: d.beds ? String(d.beds) : 'not known yet', s: d.role || '', c: '#22272E' });
  out.push({ k: 'Occupancy', v: d.occ ? d.occ + '%' : 'not known yet', s: d.occ ? ((d.trend ?? 0) >= 0 ? '▲ ' : '▼ ') + Math.abs(d.trend ?? 0) + '% trend' : '', c: d.occ && d.occ < 75 ? '#B3261E' : '#22272E' });
  out.push({ k: 'Occupancy headroom', v: d.occ ? Math.max(0, 95 - d.occ) + ' pts' : DASH, s: d.occ && d.beds ? '≈ ' + Math.round((Math.max(0, 95 - d.occ) / 100) * d.beds) + ' beds to 95% · MODELED' : '', c: '#22272E' });
  const py = (d.payers || [])[0];
  out.push({ k: 'Top payer', v: py ? py.n : 'not known yet', s: py ? fm(py.u) + (d.payersLabel ? ' · ' + d.payersLabel : '') : '', c: '#22272E' });
  const rf = (d.referrals || [])[0];
  out.push({ k: 'Top referral source', v: rf ? rf.n : 'not known yet', s: rf ? (rf.sr ?? 0) + '% of admits' : '', c: '#22272E' });
  return out;
}

const DD_TABS = ['Underwrite', 'Value-Creation', 'Risk', 'Hold-Sell', 'Performance'];

function DealDesk({ p, f }: { p: TabProps; f: Deal | null }) {
  const { set } = p;
  const st = p.st as NsState;
  const dd = st.ddTab || 'Underwrite';
  const src = st.nsDealSrc ?? null;
  const fromMap = st.nsDealFrom === 'North Star map';
  const drift = usePosition(p, src ? null : f);
  const peers = usePeerStaffing(p.client, f && !src ? [{ type: f.type, state: f.stt }] : []);
  // A map building with a catalog id: its cost-report margin, when HCRIS has it (measured).
  const uw = useQuery<FacilityUnderwrite>((signal) => intelUnderwrite(p.client, { params: { id: src!.id! }, signal }), [p.client, src?.id], { enabled: !!src?.id });
  const bt = useBuildText();
  const om = uw.data?.fields?.operatingMargin;
  const hcrisMargin = om && om.basis === 'measured' && om.value != null && om.value > 0 ? om.value : null;
  const markEV = drift ? drift.current.currentEv / 1e6 : null;
  const hasHist = !!drift?.hasRealized;
  const name = src?.name || f?.name || '';
  const showFrom = !!st.nsDealFrom;
  const fromName = fromMap ? f?.name ?? '' : st.nsDealFrom ?? '';
  const canScen = !!(src && src.beds && src.occ && (src.payers || []).reduce((s, x) => s + (x.u || 0), 0) > 0);
  const rows: [string, string][] = src
    ? mapDeskRows(src, dd, hcrisMargin)
    : !f
      ? []
      : dd === 'Underwrite'
        ? [
            ['Price', M(f.entry)],
            ['Cash-flow check (DCF)', M(f.ev)],
            ['IRR · MOIC', pc(f.irr) + ' · ' + f.moic.toFixed(1) + 'x'],
            ['Verdict', f.irr >= 0.12 ? 'Pursue' : f.irr >= 0.08 ? 'Pursue on terms' : 'Pass'],
          ]
        : dd === 'Value-Creation'
          ? [
              ['Occupancy headroom', pc(Math.max(0, 0.95 - f.occ)) + ' pts'],
              ['Agency above 7%', pc(Math.max(0, f.agency - 0.07), 1)],
              ['Payer-mix shift value', '+' + M((0.05 * 92 * f.pd) / 1e6)],
              ['Plan EBITDA uplift', '+' + M(f.ebitda * 0.18)],
            ]
          : dd === 'Risk'
            ? [
                ['Medicaid exposure', pc(f.mcaid)],
                // No federal staffing floor (rescinded 2026-02-02): nurse hours read against the peer median.
                ['Staffing (HPRD)', staffingLine(f.hprd, peerOf(peers, f.type, f.stt))],
                ['Occupancy cushion', pc(Math.max(0, f.occ - 0.75)) + ' pts over 75%'],
                ['Stress verdict', f.ev * 0.82 >= f.entry ? 'clears at ' + MINUS + '18%' : 'breaks at ' + MINUS + '18%'],
              ]
            : dd === 'Hold-Sell'
              ? [
                  ['Hold IRR (5-yr)', pc(f.irr)],
                  ['Sell now proceeds', M(f.ev - f.debt)],
                  ['Sell now MOIC', ((f.ev - f.debt) / f.eqIn).toFixed(2) + 'x'],
                  ['Recommendation', f.irr >= 0.12 ? 'HOLD' : 'SELL'],
                ]
              : [
                  ['Modeled EV at entry', M(f.entry)],
                  ['Current mark', markEV == null ? DASH : M(markEV)],
                  ['Drift vs model', markEV == null ? DASH : (markEV >= f.entry ? '+' : MINUS) + M(Math.abs(markEV - f.entry))],
                  ['Data source', hasHist ? 'HCRIS realized' : 'modeled only'],
                ];
  const facts = mapDeskFacts(src);
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;flex-wrap:wrap;')}>
        <span style={sx('font-size:16px;font-weight:700;')}>{name}</span>
        <span style={sx('font-size:14px;color:#54606C;')}>deal desk</span>
      </div>
      {showFrom && (
        <>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:-4px 0 11px;font-size:16px;color:#3C454F;line-height:1.45;')}>
            <BuildLabel><span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#3C454F;background:rgba(34,39,46,.08);border-radius:4px;padding:2px 6px;')}>MODELED</span></BuildLabel>
            <span>
              {fromMap ? 'From the North Star map:' : 'From the Relationship Map:'} <b style={sx('color:#22272E;')}>{fromName}</b>. Underwritten from its map facts with the console constants. Figures are modeled until its cost report is loaded.
            </span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (fromMap) set({ nsView: 'Map', nsDealFrom: '' });
                else p.go('relmap', { nsDealSrc: null, nsDealFrom: '' });
              }}
              style={sx('margin-left:auto;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
            >
              &larr; Back to the map
            </a>
            {canScen && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const d = src!;
                  p.go('deal', {
                    aiMapSubj: { id: d.id, name: d.name, beds: d.beds!, occ: d.occ!, role: d.role ?? '', payers: d.payers ?? [], payersLabel: d.payersLabel },
                    aiSel: 9999,
                    scOcc: undefined,
                    scMix: undefined,
                    scMargin: undefined,
                    scMult: undefined,
                  });
                }}
                style={sx('font-size:16px;font-weight:700;color:#fff;background:#2F5C97;border-radius:7px;padding:5px 11px;text-decoration:none;white-space:nowrap;')}
              >
                Run in Scenario &rarr;
              </a>
            )}
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px 18px;margin:0 0 14px;padding:10px 0;border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);')}>
            {facts.map((x) => (
              <div key={x.k} style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:600;color:#54606C;')}>{x.k}</span>
                <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + x.c + ';overflow-wrap:break-word;')}>{x.v}</span>
                <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{bt(x.s)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <div role="tablist" aria-label="Deal desk" style={sx('display:flex;align-items:center;flex-wrap:wrap;border-bottom:1px solid rgba(34,39,46,.12);margin-bottom:13px;')}>
        {DD_TABS.map((x) => (
          <A
            key={x}
            onClick={onSet(set, { ddTab: x })}
            style={'display:inline-flex;align-items:center;height:30px;padding:0 4px;margin-right:16px;font-size:16px;text-decoration:none;border-bottom:2px solid ' + (dd === x ? '#2F5C97;color:#2F5C97;font-weight:600;' : 'transparent;color:#54606C;')}
          >
            {x}
          </A>
        ))}
      </div>
      {rows.map(([label, val]) => (
        <div key={label} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('color:#3C454F;')}>{label}</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-variant-numeric:tabular-nums;font-weight:600;')}>{bt(val)}</span>
        </div>
      ))}
    </div>
  );
}

function PaperBookView({ p, held }: { p: TabProps; held: Deal[] }) {
  const book = useQuery<PaperBook>((signal) => intelPaperBook(p.client, { signal }), [p.client]);
  const prospects = p.book.data?.prospects ?? [];
  const cnt = (fn: (x: (typeof prospects)[number]) => boolean) => (p.book.data ? String(prospects.filter(fn).length) : DASH);
  const mrr = prospects.reduce((s, x) => s + (Number(x.mrr) || 0), 0);
  const entry = held.reduce((s, x) => s + x.entry, 0);
  const rows: [string, string, string][] = [
    ['Open paper positions', book.data ? String(book.data.count) : DASH, 'entry snapshot stored, re-marked quarterly'],
    ['In pilot', cnt((x) => x.lifecycle_stage === 'pilot'), 'no facility has reached pilot'],
    ['Live customers', cnt((x) => x.lifecycle_stage === 'live'), 'none live yet'],
    ['Acquired', cnt((x) => x.status === 'acquired'), 'in the roll-up portfolio'],
    ['MRR pipeline', p.book.data ? '$' + n0(mrr) : DASH, mrr ? 'signed contracts' : 'no signed contracts'],
    ['Acquisition pipeline', p.book.data ? M(entry) : DASH, 'capital deployed at entry'],
  ];
  // The notes describe the counts; when a count is not zero, say what it is instead.
  const pilot = prospects.filter((x) => x.lifecycle_stage === 'pilot').length;
  const live = prospects.filter((x) => x.lifecycle_stage === 'live').length;
  if (pilot) rows[1][2] = 'facilities in a pilot now';
  if (live) rows[2][2] = 'live on Aithora';
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
      <div style={sx(CAP + 'margin-bottom:11px;')}>Book &middot; open positions by lifecycle</div>
      {rows.map(([label, val, note]) => (
        <div key={label} style={sx('display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:6px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <span style={sx('font-size:16px;font-weight:600;')}>{label}</span>
          <span style={sx('font-size:14px;color:#54606C;')}>{note}</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;')}>{val}</span>
        </div>
      ))}
    </div>
  );
}
